import { Link } from "react-router-dom";
import AffiliateDisclosure from "@/components/AffiliateDisclosure";
import SEOHead from "@/components/SEOHead";
import { ArrowRight, Check, ChevronRight, ShieldCheck } from "lucide-react";

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "headline": "Flexible Solar Panels: RV, Van, Boat & Curved-Roof Buyer Guide",
      "description": "A practical guide to flexible solar panels for curved roofs, RVs, vans and boats, including mounting, heat, electrical fit and flexible-vs-rigid tradeoffs.",
      "url": "https://highestefficiencysolarcell.com/flexible-solar-panels",
      "dateModified": "2026-10-09",
      "author": {
        "@type": "Organization",
        "name": "Highest Efficiency Solar Cell Editorial Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Highest Efficiency Solar Cell",
        "url": "https://highestefficiencysolarcell.com"
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Are flexible solar panels worth it?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Flexible solar panels are most useful when low weight, low profile, or mild surface curvature makes a rigid framed module difficult to install. For flat permanent installations, compare rigid panels too because mounting, ventilation, serviceability, warranty and physical protection may be better."
          }
        },
        {
          "@type": "Question",
          "name": "Can flexible solar panels go on a curved roof?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Some flexible panels are designed for mild curvature, but the permitted bend radius is model-specific. Never bend a panel tighter than the current manufacturer specification."
          }
        },
        {
          "@type": "Question",
          "name": "Should flexible solar panels have an air gap?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Heat management matters because cell temperature affects electrical output and material stress. Whether an air gap is required or permitted depends on the panel and mounting system, so follow the manufacturer's mounting instructions rather than assuming every flexible panel should be bonded flat."
          }
        }
      ]
    }
  ]
};

const DECISION_ROWS = [
  ["Weight", "Usually much lighter", "Usually heavier"],
  ["Profile", "Very low profile", "Raised frame/rack"],
  ["Curved surfaces", "Possible when model allows", "Generally requires a flat mounting plane"],
  ["Ventilation", "Depends on mounting method", "Rack can provide airflow"],
  ["Removal/service", "Can be difficult if permanently bonded", "Often easier with mechanical mounts"],
  ["Physical protection", "Thin laminate needs care", "Rigid frame and glass provide structure"],
  ["Best fit", "Curved or weight-sensitive installations", "Most flat permanent installations"],
];

export default function FlexiblePanels() {
  return (
    <>
      <SEOHead
        title="Flexible Solar Panels 2026 | RV, Van, Boat & Curved Roof Guide"
        description="Compare flexible solar panels for RVs, vans, boats and curved roofs. Learn mounting, heat, bend-radius, electrical and flexible-vs-rigid buying checks."
        canonical="https://highestefficiencysolarcell.com/flexible-solar-panels"
        schema={SCHEMA}
      />

      <div className="pb-28 lg:pb-0">
        <div className="max-w-4xl mx-auto px-4 py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-foreground">Flexible Solar Panels</span>
          </nav>

          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">
            Flexible Solar Panels: RV, Van, Boat & Curved-Roof Buyer Guide
          </h1>

          <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
            Flexible solar panels solve a very specific problem: generating solar power where a heavy framed module
            is awkward because the surface is mildly curved, weight-sensitive, or needs a low profile. The best choice
            is not determined by “flexible” alone. Mounting method, heat, bend radius, roof material, electrical fit,
            serviceability, and the manufacturer's installation limits all matter.
          </p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-6">
            <span>By <Link to="/about#editorial-team" className="font-medium text-foreground hover:text-accent">Highest Efficiency Solar Cell Editorial Team</Link></span>
            <span aria-hidden="true">•</span>
            <span>Updated October 9, 2026</span>
          </div>

          <AffiliateDisclosure />

          <section className="mt-8 rounded-2xl border border-accent/30 bg-accent/5 p-5 lg:p-6">
            <h2 className="font-heading font-bold text-xl mb-2">Quick answer: when should you buy a flexible solar panel?</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Choose a flexible panel when low weight, low profile, or manufacturer-approved curvature solves a real installation problem.
              If the surface is flat and a rigid module fits, compare rigid panels too. A rigid frame can make ventilation, mechanical mounting,
              replacement, and long-term physical protection simpler. For either type, verify Voc, Vmp, Isc, Imp, dimensions, weight,
              connector, mounting instructions, warranty, and the limits of the charge controller or power station.
            </p>
          </section>

          <div className="prose prose-lg max-w-none mt-10 space-y-8 text-muted-foreground">
            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">What are flexible solar panels?</h2>
              <p>
                Flexible solar panels use photovoltaic cells inside a thin laminated construction instead of a conventional
                rigid glass-and-aluminum frame. “Flexible” does not mean a panel can be folded, rolled, or sharply bent at will.
                The allowed curvature is model-specific. Some modules are intended only for a gentle arc, and repeated bending
                may not be allowed at all after installation. Read the current datasheet and mounting guide for the exact module
                before assuming it will conform to a roof, deck, fairing, or vehicle panel.
              </p>
              <p>
                This distinction matters for buyers searching for bendable solar panels. The useful question is not
                “How flexible is it?” but “Does the manufacturer's permitted bend radius match the actual surface, and can
                the module be mounted without concentrating stress at edges, cable exits, or attachment points?”
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">Flexible vs rigid solar panels: the decision that matters</h2>
              <div className="not-prose overflow-x-auto rounded-2xl border border-border">
                <table className="w-full min-w-[640px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-foreground text-background">
                      <th className="py-3 px-4 text-left font-mono text-[10px] uppercase tracking-wider">Factor</th>
                      <th className="py-3 px-4 text-left font-mono text-[10px] uppercase tracking-wider">Flexible</th>
                      <th className="py-3 px-4 text-left font-mono text-[10px] uppercase tracking-wider">Rigid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DECISION_ROWS.map(([factor, flexible, rigid]) => (
                      <tr key={factor} className="border-t border-border">
                        <td className="py-3 px-4 font-medium text-foreground">{factor}</td>
                        <td className="py-3 px-4">{flexible}</td>
                        <td className="py-3 px-4">{rigid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">Flexible solar panels for RV and van roofs</h2>
              <p>
                Mobile roofs are where flexible panels make the strongest case. RVs, pop-up campers, vans, and molded fiberglass
                roofs can have vents, fans, compound curves, narrow channels, and strict weight limits. Make a roof map before buying:
                measure usable rectangles, note shade from rooftop equipment, identify cable-entry points, and mark the areas where
                curvature changes. Then compare the panel's dimensions and permitted curvature with that map.
              </p>
              <p>
                For a van or RV, electrical fit matters just as much as roof fit. Compare panel Voc and current with the charge
                controller or power station limits, and decide whether multiple panels will be wired in series or parallel before
                ordering cables and branch connectors. For a deeper mobile-roof guide, see our{" "}
                <Link to="/solar-guides/best-flexible-solar-panels-for-rv-roofs" className="text-accent-text hover:underline">
                  flexible solar panels for RV roofs guide
                </Link>{" "}
                and our <Link to="/solar-panels-for-van-life" className="text-accent-text hover:underline">van-life solar guide</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">Mounting adhesive, tape, and the heat question</h2>
              <p>
                A low-profile panel can be mounted in several ways depending on the product: mechanical fasteners, approved adhesive,
                high-bond tape, a removable mounting sheet, or a manufacturer-specific bracket or backing system. Do not choose an adhesive
                solely because it sticks to the panel. It also has to be compatible with the roof membrane, fiberglass, painted metal,
                gelcoat, or other substrate underneath, and it has to tolerate the expected temperature and weather exposure.
              </p>
              <p>
                Heat deserves special attention. Solar cells change electrical behavior as temperature changes, and a panel bonded directly
                to a hot surface can operate differently from one with airflow underneath. That does not create one universal “required air gap.”
                Follow the panel maker's mounting instructions and any roof-material guidance. If the documentation requires ventilation,
                spacers, or a particular adhesive pattern, treat that as part of the electrical installation, not cosmetic advice.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">Flexible solar panels on fiberglass roofs and boats</h2>
              <p>
                Fiberglass and marine installations add two concerns: surface preparation and moisture exposure. A mounting method that works
                on painted metal may not be appropriate for gelcoat or a textured fiberglass roof. Cleaners, primers, adhesives, cable glands,
                and sealants should all be compatible with the substrate and the panel manufacturer's instructions. Plan how the panel will be
                removed later before making a permanent bond.
              </p>
              <p>
                On boats, salt exposure and repeated wetting make connector sealing, cable routing, corrosion-resistant hardware, and strain
                relief especially important. Do not assume a flexible panel is “marine rated” unless the current documentation says so.
                For more detail, see our{" "}
                <Link to="/solar-guides/flexible-solar-panels-for-boats-buying-guide" className="text-accent-text hover:underline">
                  flexible solar panels for boats guide
                </Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">How to compare flexible solar panels before buying</h2>
              <ul className="space-y-2">
                {[
                  "Measure the actual usable mounting area, including vents, curves and cable exits.",
                  "Verify the manufacturer's minimum bend radius or maximum permitted curvature.",
                  "Check whether the mounting instructions allow adhesive, tape, mechanical fasteners, or a ventilated backing.",
                  "Compare Voc, Vmp, Isc and Imp with the charge controller or power station.",
                  "Confirm connector type and polarity instead of assuming a cable labeled 'solar' is compatible.",
                  "Check weight and deployed dimensions against the roof or deck limits.",
                  "Read the current warranty for mounting exclusions, foot-traffic rules, flexing limits and environmental restrictions.",
                  "Plan removal and service before making a permanent bond."
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-accent mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-2xl text-foreground">Frequently asked questions</h2>
              <div className="not-prose space-y-4">
                {[
                  {
                    q: "Are flexible solar panels worth it?",
                    a: "They are worth considering when low weight, low profile, or mild manufacturer-approved curvature solves an installation problem. For flat permanent installations, compare rigid panels too because mounting, ventilation and serviceability may be simpler."
                  },
                  {
                    q: "Can flexible solar panels be walked on?",
                    a: "Do not walk on a flexible panel unless the manufacturer explicitly rates that exact model for foot traffic. A thin laminate can suffer cell damage even when the outer surface looks intact."
                  },
                  {
                    q: "Can flexible solar panels go on a curved roof?",
                    a: "Some can, within the exact model's permitted bend radius or curvature specification. Never force a panel to match a tighter curve than the manufacturer allows."
                  },
                  {
                    q: "Should I glue a flexible panel directly to an RV or van roof?",
                    a: "Only when the panel and roof manufacturers permit that mounting method. Surface compatibility, heat management, future removal, water sealing and warranty conditions all matter."
                  },
                  {
                    q: "Are flexible solar panels good for boats?",
                    a: "They can be useful where low profile or mild curvature matters, but verify environmental ratings, mounting instructions, connector sealing and corrosion considerations for the exact product."
                  }
                ].map((faq) => (
                  <div key={faq.q} className="border border-border rounded-xl p-5">
                    <h3 className="font-heading font-semibold mb-2 text-foreground text-base">{faq.q}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="mt-10 rounded-2xl border border-border bg-secondary/20 p-5 lg:p-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading font-bold text-xl">Editorial method</h2>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  This is a specification-based buyer guide. We do not claim that every flexible panel shares the same efficiency,
                  lifespan, bend radius, waterproof rating, or mounting method. Those details are model-specific and should be verified
                  against current manufacturer documentation before purchase or installation.
                </p>
                <Link to="/about#editorial-process" className="inline-block mt-2 text-sm text-accent-text hover:underline">
                  Read our editorial process
                </Link>
              </div>
            </div>
          </section>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <Link
              to="/solar-guides/best-flexible-solar-panels-for-rv-roofs"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
            >
              Flexible Panels for RV Roofs <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/best-high-efficiency-solar-panels"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
            >
              Compare High-Efficiency Panels
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
